/**
 * The room's fixed values the page needs, each mirroring one in `WebinarRoomLimits`, `WebinarRoomActorKeys`,
 * `WebinarRoomUrlBuilder`, `WebinarRoomPageBuilder` or `WebinarRoomTranscript` (api/OrbitalX.Business): change one,
 * change the other.
 * contract.test.ts reads the C# to hold them together.
 */

/** `WebinarRoomLimits.HeartbeatSeconds`: how often a viewer's page reports what it is watching. */
export const HEARTBEAT_SECONDS = 30

/** `WebinarRoomLimits.DoorNameMaxLength`: the longest name the door accepts. */
export const DOOR_NAME_MAX_LENGTH = 100

/** `WebinarRoomLimits.ChatMaxLength` and `AnswerMaxLength`: the longest message, question or answer the API takes. */
export const CHAT_MAX_LENGTH = 1000
export const ANSWER_MAX_LENGTH = 1000

/** `WebinarRoomLimits.PollQuestionMaxLength`, `PollOptionMaxLength`, `PollMinOptions` and `PollMaxOptions`: a poll written live. */
export const POLL_QUESTION_MAX_LENGTH = 200
export const POLL_OPTION_MAX_LENGTH = 120
export const POLL_MIN_OPTIONS = 2
export const POLL_MAX_OPTIONS = 4

/**
 * `WebinarRoomLimits.PollStaysUpThirtySeconds`, `PollStaysUpOneMinute`, `PollStaysUpTwoMinutes` and `PollStaysUpFiveMinutes`:
 * how long a poll may stay up before it closes by itself. A poll without one stays up until the next poll.
 */
export const POLL_STAYS_UP_SECONDS = [30, 60, 120, 300] as const

/** `WebinarRoomLimits.ViewerLatencySeconds`: a timed poll launches this long after its time in the recording. */
export const VIEWER_LATENCY_SECONDS = 6

/**
 * The stream job's `StreamMode.EndedBeforeTheRecording` slack, `2 * FfmpegArguments.KeyframeSeconds + 1`: it reports a
 * run completed only this close to the recording's end, so a stream that ended further short of it went down mid-video.
 */
export const VIDEO_END_SLACK_SECONDS = 5

/** `WebinarRoomLimits.RestartDedupeSeconds`: a Restart video this soon after the last one is taken as the same restart. */
export const RESTART_DEDUPE_SECONDS = 60

/** `WebinarRoomPollRunDto.LaunchedByName` for a poll the stream's position launched, rather than a person. */
export const AUTO_LAUNCHED_BY = 'Auto'

/** `WebinarRoomActorKeys`: a viewer's AuthorKey is `{roomId}:v:{registrationId}`. */
export const ACTOR_KEY_SEPARATOR = ':'
export const VIEWER_ACTOR_KIND = 'v'

/** `WebinarRoomUrlBuilder`'s fragment keys: a registrant's link, a presenter's host link and a moderator's preview. */
export const FRAGMENT_KEYS = {
    viewer: 't',
    host: 'h',
    preview: 'p',
} as const

/** `WebinarRoomPageBuilder.ConfigElementId` and `RootElementId`: where the page carries its config and where the bundle renders. */
export const CONFIG_ELEMENT_ID = 'ox-room'
export const ROOT_ELEMENT_ID = 'ox-room-root'

/**
 * `WebinarRoomTranscript.FileName` for a session with no start: the transcript's name when a download does not say
 * one. Every other name it gives starts the same way, with the session's day after it.
 */
export const TRANSCRIPT_FALLBACK_FILE_NAME = 'chat-and-questions.csv'

/**
 * `WebinarRoomLimits.SubtitleLanguageCodes`: the spoken languages a recording's subtitles may be in, one per recording
 * (ORB-1132), with the name the Room tab and the CC button's title give each.
 */
export const SUBTITLE_LANGUAGES = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    pt: 'Portuguese',
    it: 'Italian',
} as const

export type SubtitleLanguageCode = keyof typeof SUBTITLE_LANGUAGES

/** `WebinarRoomLimits.MaxSubtitleBytes` and `SubtitleFileExtensions`: what the Room tab checks before the API checks again. */
export const SUBTITLE_MAX_BYTES = 1024 * 1024
export const SUBTITLE_FILE_EXTENSIONS = ['.srt', '.vtt'] as const
